/**
 * Download an application's mirror repo as a zip.
 *
 * Split out of `DownloadRepoButton` (#2194) because the action moved into
 * a menu, and a menu item cannot carry the button's inline error span —
 * the menu has closed by the time the request fails. The guard, the
 * loading flag and the request are the parts worth keeping; where the
 * failure is shown is the caller's business.
 *
 * `DownloadRepoButton` was the only other caller and went with the change,
 * so this is the one download path rather than one per shape.
 */
import { useState } from 'react'
import { downloadFromUrl } from '@/lib/downloadFromUrl'
import { workspaceApiUrl } from '@/lib/api'

export interface RepoDownload {
  /** Start the download. Resolves with `false` when it failed, so a caller
   * that shows its own message does not have to catch. */
  download: () => Promise<boolean>
  loading: boolean
  /** The last failure, or null. A caller rendering inline reads this; one
   * using a toast can ignore it and read `download`'s result. */
  error: string | null
}

export function useRepoDownload(applicationId: string): RepoDownload {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download(): Promise<boolean> {
    // Guard re-entrancy: the trigger is disabled while loading, but a very
    // fast double-click can fire before that re-renders — without this it
    // is two downloads (Copilot review, #2055). A menu item cannot be
    // disabled mid-click at all, so the guard matters more here than it
    // did on the button.
    if (loading || !applicationId) return false
    setLoading(true)
    setError(null)
    try {
      // Workspace-scoped: the applications router mounts under
      // /api/workspaces/:workspaceId/…, so the URL needs the active
      // workspace prefix. The filename comes from Content-Disposition.
      await downloadFromUrl(
        workspaceApiUrl(`applications/${applicationId}/repo.zip`),
        'repo.zip',
      )
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      return false
    } finally {
      setLoading(false)
    }
  }

  return { download, loading, error }
}
